<template>
  <div class="block-form">
    <div class="form-label" style="margin-bottom: 4px;">Preguntas</div>

    <div
      v-for="(q, qIdx) in block.questions"
      :key="qIdx"
      class="card-block"
    >
      <div class="card-header">
        <span class="card-number">Pregunta {{ qIdx + 1 }}</span>
        <q-btn
          flat
          dense
          round
          icon="delete"
          color="negative"
          size="sm"
          :disable="block.questions.length <= 1"
          @click="removeQuestion(qIdx)"
        >
          <q-tooltip>
            {{ block.questions.length <= 1 ? 'Debe haber al menos una pregunta' : 'Eliminar pregunta' }}
          </q-tooltip>
        </q-btn>
      </div>

      <q-input
        outlined
        dense
        type="textarea"
        rows="2"
        :model-value="q.question"
        label="Enunciado"
        @update:model-value="updateQuestion(qIdx, { question: String($event ?? '') })"
      />

      <div class="form-label">Opciones (marcá la correcta)</div>
      <div
        v-for="(opt, optIdx) in q.options"
        :key="`${qIdx}-${optIdx}`"
        class="option-row"
      >
        <q-radio
          :model-value="q.correctIndex"
          :val="optIdx"
          color="teal"
          size="sm"
          @update:model-value="updateQuestion(qIdx, { correctIndex: optIdx })"
        >
          <q-tooltip>Marcar como correcta</q-tooltip>
        </q-radio>
        <q-input
          outlined
          dense
          :model-value="opt"
          :label="`Opción ${optIdx + 1}`"
          style="flex: 1;"
          @update:model-value="updateOption(qIdx, optIdx, String($event ?? ''))"
        />
        <q-btn
          flat
          dense
          round
          icon="delete"
          color="negative"
          size="sm"
          :disable="q.options.length <= 2"
          @click="removeOption(qIdx, optIdx)"
        >
          <q-tooltip>
            {{ q.options.length <= 2 ? 'Debe haber al menos dos opciones' : 'Eliminar opción' }}
          </q-tooltip>
        </q-btn>
      </div>

      <q-btn
        flat
        no-caps
        icon="add"
        label="Agregar opción"
        color="primary"
        size="sm"
        @click="addOption(qIdx)"
      />

      <q-input
        outlined
        dense
        type="textarea"
        rows="2"
        :model-value="q.explanation ?? ''"
        label="Explicación (opcional)"
        hint="Se muestra al alumno tras responder"
        @update:model-value="updateQuestion(qIdx, { explanation: String($event ?? '') })"
      />
    </div>

    <q-btn
      flat
      no-caps
      icon="add"
      label="Agregar pregunta"
      color="primary"
      size="sm"
      @click="addQuestion"
    />
  </div>
</template>

<script setup lang="ts">
interface QuizQuestion {
  question: string;
  options: string[];
  correctIndex: number;
  explanation?: string;
}

interface QuizBlockData {
  type: 'quiz';
  questions: QuizQuestion[];
}

const props = defineProps<{ block: QuizBlockData }>();
const emit = defineEmits<{ (e: 'update', block: QuizBlockData): void }>();

function updateQuestion(idx: number, patch: Partial<QuizQuestion>) {
  const questions = props.block.questions.map((q, i) =>
    i === idx ? { ...q, ...patch } : q,
  );
  emit('update', { ...props.block, questions });
}

function addQuestion() {
  const questions = [
    ...props.block.questions,
    { question: '', options: ['', ''], correctIndex: 0, explanation: '' },
  ];
  emit('update', { ...props.block, questions });
}

function removeQuestion(idx: number) {
  // Guardrail: al menos una pregunta (no hay validacion server-side del shape).
  if (props.block.questions.length <= 1) return;
  const questions = props.block.questions.filter((_, i) => i !== idx);
  emit('update', { ...props.block, questions });
}

function updateOption(qIdx: number, optIdx: number, value: string) {
  const options = props.block.questions[qIdx].options.map((o, i) =>
    i === optIdx ? value : o,
  );
  updateQuestion(qIdx, { options });
}

function addOption(qIdx: number) {
  const options = [...props.block.questions[qIdx].options, ''];
  updateQuestion(qIdx, { options });
}

function removeOption(qIdx: number, optIdx: number) {
  // Guardrail: al menos dos opciones por pregunta.
  const current = props.block.questions[qIdx];
  if (current.options.length <= 2) return;
  const options = current.options.filter((_, i) => i !== optIdx);
  // Re-clamp de correctIndex para que nunca quede fuera de rango tras el borrado.
  const correctIndex = Math.min(current.correctIndex, options.length - 1);
  updateQuestion(qIdx, { options, correctIndex });
}
</script>

<style scoped lang="scss">
.block-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
}
.form-label {
  font-size: 13px;
  color: var(--oa-text-muted);
  margin-bottom: 6px;
}
.card-block {
  border: 1px solid var(--oa-border);
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: var(--oa-bg-2);
}
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.card-number {
  font-size: 13px;
  font-weight: 600;
  color: var(--oa-text-secondary);
}
.option-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
